// Shapes and pure translation logic for OS-2345's Map fields step. Kept separate from the
// step/row components so the save/load translation (the part with real backend-shape and
// backend-gap consequences -- see frd.md/technical-spec.md) can be unit tested directly,
// without rendering the wizard or OspreyFieldSelect.

export type OspreyFieldSelectType = 'Practice' | 'Client' | 'Matter' | 'Custom Data' | 'Formula'

// Mirrors OspreyFieldSelect's own controlled value shape (OS-2351) -- only the properties
// relevant to `type` are ever populated by that component, the rest stay null.
export type OspreyFieldSelectValue = {
  type: OspreyFieldSelectType | null
  tableName: string | null
  fieldName: string | null
  repetitionTableName: string | null
  repetitionFieldName: string | null
  customDataPageId: string | null
  customDataFieldId: string | number | null
  formulaExpression: string | null
}

export type FormField = {
  formFieldId: string
  formId: string
  name: string
  description: string | null
  fieldType: string
  pageIndex: number
  tabOrder: number
  isReadOnly: boolean
  isAutoCompleted: boolean
  isRequired: boolean
  fieldMask: string | null
  isPersonal: boolean
  createdDate: string
  updatedDate: string
}

export type MappedFormField = {
  mappedFormFieldId: string | null
  mappedFormId: string
  formFieldId: string
  mappingType: string
  customDataPageId: string | null
  fieldName: string | null
  formulaId: string | null
}

// GetAllExtendedByMappedFormId's own response shape -- MappedFormField's fields plus the
// display-only fields technical-spec.md names (fieldDescription/customDataPageDescription/
// formFieldName). mappedFormFieldId/formFieldId are read here too -- needed to key each row
// back to its own right-column FormField and to upsert correctly on save.
export type MappedFormFieldExtended = MappedFormField & {
  fieldDescription?: string | null
  customDataPageDescription?: string | null
  formFieldName?: string | null
}

const EMPTY_VALUE: OspreyFieldSelectValue = {
  type: null,
  tableName: null,
  fieldName: null,
  repetitionTableName: null,
  repetitionFieldName: null,
  customDataPageId: null,
  customDataFieldId: null,
  formulaExpression: null
}

export const CLIENT_NAMED_UNSAVEABLE_MESSAGE =
  "Named client / phone number mappings can't be saved yet — a backend change is needed first"
export const FORMULA_UNSAVEABLE_MESSAGE = "Formula mappings can't be saved yet — a backend change is needed first"

// The exact per-type completeness rule technical-spec.md defines (reusing the same
// per-type requirements OspreyFieldSelect's own five branches already imply).
export const isFieldSelectValueComplete = (value?: OspreyFieldSelectValue | null): boolean => {
  if (!value?.type) return false

  switch (value.type) {
    case 'Practice':
    case 'Matter':
      return Boolean(value.tableName && value.fieldName)
    case 'Client':
      if (!value.tableName || !value.fieldName) return false
      if (value.tableName === 'cliname') return Boolean(value.repetitionTableName && value.repetitionFieldName)
      return true
    case 'Custom Data':
      return Boolean(value.customDataPageId && (value.customDataFieldId || value.customDataFieldId === 0))
    case 'Formula':
      return Boolean(value.formulaExpression && value.formulaExpression.trim().length > 0)
    default:
      return false
  }
}

// The two confirmed backend gaps (frd.md's "Genuinely open"): a cliname/linkname/cliphone
// sourced Client field, or any Formula field, cannot be saved today. Returns the honest
// per-row disclosure message for either case, null when the row is saveable.
export const getUnsaveableReason = (value?: OspreyFieldSelectValue | null): string | null => {
  if (!value?.type) return null
  if (value.type === 'Client' && value.tableName === 'cliname') return CLIENT_NAMED_UNSAVEABLE_MESSAGE
  if (value.type === 'Formula') return FORMULA_UNSAVEABLE_MESSAGE
  return null
}

// Save-side translation (technical-spec.md's "Translating one row's OspreyFieldSelect value
// to a MappedFormField"). Returns null for an incomplete row or one of the two unsaveable
// categories -- the caller excludes those from the CreateOrUpdateAll payload rather than
// sending a request the server will reject.
export const toMappedFormField = ({
  value,
  formFieldId,
  mappedFormId,
  mappedFormFieldId = null
}: {
  value: OspreyFieldSelectValue | null | undefined
  formFieldId: string
  mappedFormId: string | null
  mappedFormFieldId?: string | null
}): MappedFormField | null => {
  if (!isFieldSelectValueComplete(value) || getUnsaveableReason(value) || !value) return null

  const base = {
    mappedFormFieldId,
    mappedFormId,
    formFieldId,
    customDataPageId: null,
    fieldName: null,
    formulaId: null
  }

  if (value.type === 'Practice' || value.type === 'Matter') {
    return { ...base, mappingType: value.type, fieldName: value.fieldName }
  }

  if (value.type === 'Client') {
    // tableName === 'cliname' already excluded above via getUnsaveableReason
    return { ...base, mappingType: 'Client', fieldName: value.fieldName }
  }

  if (value.type === 'Custom Data') {
    return {
      ...base,
      mappingType: 'Custom Data',
      customDataPageId: value.customDataPageId,
      fieldName: String(value.customDataFieldId)
    }
  }

  return null
}

// Load-side translation (technical-spec.md's "Loading existing values"), the mirror image
// of the save-side translation above. A Formula row has no live formulaId -> expression
// lookup in this ticket's own scope, so it reconstructs with no expression -- the row
// component renders that case read-only/disabled rather than guessing an expression.
export const fromMappedFormFieldExtended = (row: MappedFormFieldExtended): OspreyFieldSelectValue | null => {
  switch (row.mappingType) {
    case 'Practice':
      return { ...EMPTY_VALUE, type: 'Practice', tableName: 'practiceinfo', fieldName: row.fieldName }
    case 'Matter':
      return { ...EMPTY_VALUE, type: 'Matter', tableName: 'matter', fieldName: row.fieldName }
    case 'Client':
      // The repository's own "client" conversion is one-way lossy -- a saved Client row's
      // original client vs cliname source table can't be told apart from what comes back
      // here, which matches the fact that only client-table rows can be saved at all today.
      return { ...EMPTY_VALUE, type: 'Client', tableName: 'client', fieldName: row.fieldName }
    case 'Custom Data':
      return {
        ...EMPTY_VALUE,
        type: 'Custom Data',
        customDataPageId: row.customDataPageId,
        customDataFieldId: row.fieldName != null ? Number(row.fieldName) : null
      }
    case 'Formula':
      return { ...EMPTY_VALUE, type: 'Formula', formulaExpression: null }
    default:
      return null
  }
}
